import { LucideIcon } from 'lucide-react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface Trend {
  percentage: number;
  isPositive: boolean;
  hasData: boolean;
}

interface MetricCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  trend?: Trend;
  description?: string;
}

export function MetricCard({ title, value, icon: Icon, trend, description }: MetricCardProps) {
  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-3 sm:p-4 lg:p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] group w-full min-h-[140px] sm:min-h-[160px] lg:min-h-[180px] flex flex-col">
      {/* Header section with responsive structure */}
      <div className="flex items-start justify-between mb-2 sm:mb-3 lg:mb-4 min-h-[40px] sm:min-h-[48px] lg:min-h-[64px]">
        <div className="flex-1 pr-2 sm:pr-3">
          <p className="text-gray-600 font-medium text-xs sm:text-sm lg:text-base mb-1 sm:mb-2 leading-tight">
            {title}
          </p>
          {description && (
            <div className="hidden sm:block lg:block">
              <p className="text-gray-500 text-xs leading-tight overflow-hidden text-ellipsis" 
                 style={{
                   display: '-webkit-box',
                   WebkitLineClamp: 2,
                   WebkitBoxOrient: 'vertical' as const,
                   maxHeight: '2.4em'
                 }}>
                {description}
              </p>
            </div>
          )}
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2 sm:p-2.5 lg:p-3 rounded-lg group-hover:from-blue-100 group-hover:to-blue-200 transition-colors duration-300 flex-shrink-0">
          <Icon className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-blue-600" />
        </div>
      </div>
      
      {/* Spacer to push KPI section to bottom */}
      <div className="flex-1"></div>
      
      {/* KPI section with responsive positioning */}
      <div className="flex items-center justify-between min-h-[32px] sm:min-h-[36px] lg:min-h-[40px] mt-auto">
        <div className="flex-1 min-w-0">
          <p className="text-gray-900 font-bold text-lg sm:text-xl lg:text-2xl tracking-tight leading-none truncate">
            {value}
          </p>
        </div>
        
        {/* Responsive trend area */}
        <div className="ml-2 sm:ml-3 flex items-center justify-end flex-shrink-0">
          {trend && trend.hasData && (
            <div className={`flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs ${
              trend.isPositive 
                ? 'bg-green-50 text-green-700' 
                : 'bg-red-50 text-red-700'
            }`}>
              {trend.isPositive ? (
                <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
              ) : (
                <TrendingDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
              )}
              <span className="font-medium whitespace-nowrap text-xs">
                {Math.abs(trend.percentage).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}